import { useEffect } from 'react';
import { postMessageToParent, useKsFromHost } from '@selfserve/react-common-data';
import { useLoginForUpgrade } from '../../../../react-feature-pricing/src/lib';
import { useAuth, useLogoutRequest } from '@selfserve/react-hooks';
import { apiEndpoints, isTrustedHostOrigin } from '@selfserve/react-utils';

const AVATAR_STUDIO = 'AVATAR_STUDIO';

const postLoginCompleted = (success: boolean) => {
  postMessageToParent({
    id: 'unisphere.services.iframes.emit',
    version: '1.0.0',
    payload: {
      id: 'unisphere.widget.plg.events.ssrv-login-completed',
      version: '1.0.0',
      payload: {
        success
      },
    }
  });
};

export const IframeContentHelper = () => {
  // the ks is sent by the parent (ssrv-bridge runtime) instead of the url
  const { ks, isResolved: isKsResolved } = useKsFromHost(
    'unisphere.widget.plg.events.ssrv-helper-ks-requested',
    true
  );
  const { mutate: loginForUpgrade } = useLoginForUpgrade();
  const { mutate: handleLogoutRequest } = useLogoutRequest();
  const { setHasAccessToken } = useAuth();
  // opt out mixpanel if account has no ssrv subscription
  useEffect(() => {
    if (!ks) return;
    const controller = new AbortController();
    fetch(apiEndpoints.getUsageSummary, {
      method: 'POST',
      body: new URLSearchParams({ productType: AVATAR_STUDIO, ks }),
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.id === 10010) {
          (window as any).mixpanel?.ssrv?.opt_out_tracking();
        } else {
          (window as any).mixpanel?.ssrv?.opt_in_tracking();
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [ks]);

  // handle login
  useEffect(() => {
    if (!isKsResolved) return;
    if (!ks) {
      console.log('Failed to login, no ks received');
      postLoginCompleted(false);
      return;
    }
    let success = false;
    loginForUpgrade({ productFamily: AVATAR_STUDIO, ks }, {
      onSuccess: (res) => {
        success = res.status >= 200 && res.status < 300;
      },
      onError: () => {
        console.log('Failed to login');
      },
      onSettled: () => postLoginCompleted(success)
    });
  }, [ks, isKsResolved]);

  // handle logout
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // only the page that embeds the iframe on a trusted origin can trigger logout
      if (event.source !== window.parent || !isTrustedHostOrigin(event.origin))
        return;

      const message = event.data;

      // Check if message has the expected structure
      if (message && message.action === 'logout') {
        // Handle logout action
        console.log('Logout action received');
        let success = false;
        // end the session on kaltura.com - the pricing tab cookie can be partitioned under another domain so this logout cannot clear it
        handleLogoutRequest({ endSession: true }, {
          onSuccess: (res) => {
            success = res.status >= 200 && res.status < 300;
            if (success) {
              // the logout clears the access_token cookie - clear hasAccessToken flag too
              setHasAccessToken(false);
            }
          },
          onError: () => {
            console.log('Failed to logout');
          },
          onSettled: () => {
            postMessageToParent({
              id: 'unisphere.services.iframes.emit',
              version: '1.0.0',
              payload: {
                id: 'unisphere.widget.plg.events.ssrv-logout-completed',
                version: '1.0.0',
                payload: {
                  success
                },
              }
            });
          }
        });
      }
    };

    // Add event listener
    window.addEventListener('message', handleMessage);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  return null;
}
